import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Area, Column } from "@ant-design/charts";
import { Spin } from "antd";
import { LineChartOutlined, BarChartOutlined } from "@ant-design/icons";
import { getOperationalHealthTrends, getDepartmentEfficiency } from "../../api/plans/GetExecutiveAnalyticsApi";
import GetPerformanceStatsApi from "../../api/performance/GetPerformanceStatsApi";
import Theme from "../../theme/Theme";

function ExecutiveStethoscope() {
  const [activeTab, setActiveTab] = useState("health");
  const [healthData, setHealthData] = useState([]);
  const [deptData, setDeptData] = useState([]);
  const [orgPerformance, setOrgPerformance] = useState(0);
  const [loading, setLoading] = useState(false);

  const perfCycleState = useSelector((state) => state.perfCycleState.value);
  const positionState = useSelector((state) => state.positionState.value);

  useEffect(() => {
    if (perfCycleState?.id && positionState?.id) {
      loadData();
    }
  }, [perfCycleState?.id, positionState?.id]);

  async function loadData() {
    setLoading(true);
    try {
      const [healthRes, deptRes, perfRes] = await Promise.all([
        getOperationalHealthTrends(perfCycleState.id, positionState.id),
        getDepartmentEfficiency(perfCycleState.id, positionState.id),
        GetPerformanceStatsApi(positionState.id, perfCycleState.id),
      ]);
      if (deptRes.success) setDeptData(deptRes.data || []);
      
      // Use the REAL performance from the carousel's API
      const realOrgPerf = parseFloat(perfRes?.data?.organization?.performance) || 0;
      
      if (healthRes.success && healthRes.data?.length > 0) {
        // Add a starting point at 0 and fill in health/velocity with real performance
        const rawData = healthRes.data;
        const totalPast = rawData.filter(d => d.operationalHealth !== null).length;
        
        // Start point
        const startPoint = { month: "Start", operationalHealth: 0, progressVelocity: 0, target: 0 };
        
        const fixedData = rawData.map((item, idx) => {
          const progress = totalPast > 0 ? ((idx + 1) / totalPast) * realOrgPerf : 0;
          const health = item.operationalHealth !== null ? Math.min(progress, realOrgPerf) : null;
          
          return {
            ...item,
            operationalHealth: health,
            progressVelocity: health !== null ? +(health * 0.85).toFixed(1) : null,
          };
        });
        
        setHealthData([startPoint, ...fixedData]);
      } else {
        setHealthData(healthRes?.data || []);
      }
    } catch (e) {
      console.error("Executive analytics error:", e);
    } finally {
      setLoading(false);
    }
  }

  const tabs = [
    { key: "health", label: "Operational Health Trends", icon: <LineChartOutlined /> },
    { key: "dept", label: "Department Efficiency Matrix", icon: <BarChartOutlined /> },
  ];

  const latestHealth = healthData.length > 0 
    ? parseFloat(healthData[healthData.length - 1]?.operationalHealth) || 0
    : 0;

  // Transform health data for the area chart - order matters for layering
  const areaChartData = healthData.flatMap((item) => [
    { month: item.month, value: item.target, category: "Operational Target" },
    { month: item.month, value: item.progressVelocity, category: "Progress Velocity" },
    { month: item.month, value: item.operationalHealth, category: "Operational Health" },
  ]);

  // Health-index color: orange < 40%, yellow 40-70%, green >= 70%
  const healthColor = latestHealth >= 70 ? "#22c55e" : latestHealth >= 40 ? "#f59e0b" : "#F15A24";
  const healthColorRgb = latestHealth >= 70 ? "34,197,94" : latestHealth >= 40 ? "245,158,11" : "241,90,36";

  const areaConfig = {
    data: areaChartData.filter(d => d.value !== null),
    xField: "month",
    yField: "value",
    seriesField: "category",
    smooth: true,
    animation: false,
    isStack: false,
    connectNulls: false,
    yAxis: {
      min: 0,
      max: 100,
      tickCount: 5,
      grid: { line: { style: { stroke: "#f5f5f5", lineWidth: 1 } } },
      label: { style: { fontSize: 10, fill: "#bbb" } },
    },
    xAxis: {
      line: { style: { stroke: "#f5f5f5" } },
      label: { style: { fontSize: 10, fill: "#bbb" } },
    },
    legend: {
      position: "bottom",
      marker: { symbol: "circle", style: { r: 4 } },
      itemName: { style: { fontSize: 11, fill: "#888" } },
      offsetY: 10,
    },
    color: ["#1e293b", "#94a3b8", healthColor],
    areaStyle: (datum) => {
      if (datum.category === "Operational Health") {
        return { fill: `l(90) 0:rgba(${healthColorRgb},0) 1:rgba(${healthColorRgb},0.35)` };
      }
      return { fillOpacity: 0, fill: "rgba(0,0,0,0)" };
    },
    lineStyle: (datum) => {
      if (datum.category === "Operational Target") {
        return { lineDash: [6, 4], lineWidth: 2, stroke: "#1e293b" };
      }
      if (datum.category === "Progress Velocity") {
        return { lineDash: [3, 3], lineWidth: 1.5, stroke: "#94a3b8" };
      }
      return { lineWidth: 2.5 };
    },
    point: {
      size: 3,
      shape: "circle",
      style: (datum) => {
        if (datum.category === "Operational Health") {
          return { fill: healthColor, stroke: "#fff", lineWidth: 1.5 };
        }
        return { fill: "transparent", stroke: "transparent" };
      },
    },
    tooltip: {
      showMarkers: true,
      domStyles: {
        "g2-tooltip": {
          backgroundColor: "#1e293b",
          color: "#fff",
          borderRadius: "8px",
          padding: "12px 16px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          fontSize: "11px",
        },
      },
    },
    height: 260,
  };

  // Transform dept data for grouped column chart
  const columnChartData = deptData.flatMap((item) => [
    { department: item.department, value: item.budgetUtilization, metric: "Budget Utilization" },
    { department: item.department, value: item.efficiencyIndex, metric: "Efficiency Index" },
    { department: item.department, value: item.strategicAlignment, metric: "Strategic Alignment" },
  ]);

  const columnConfig = {
    data: columnChartData,
    xField: "department",
    yField: "value",
    seriesField: "metric",
    isGroup: true,
    animation: false,
    yAxis: {
      min: 0,
      max: 100,
      grid: { line: { style: { stroke: "#f0f0f0", lineWidth: 1 } } },
    },
    legend: {
      position: "bottom",
      marker: { symbol: "circle", style: { r: 4 } },
    },
    color: ["#94a3b8", Theme().primary, "#f59e0b"],
    columnStyle: { radius: [4, 4, 0, 0] },
    tooltip: {
      domStyles: {
        "g2-tooltip": {
          backgroundColor: "#1e293b",
          color: "#fff",
          borderRadius: "8px",
          padding: "12px 16px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        },
      },
    },
    height: 260,
  };

  return (
    <div
      className="mb-6 rounded-xl"
      style={{
        backgroundColor: "white",
        border: `1px solid ${Theme().navBorder}`,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div className="p-6 pb-4">
        <div className="flex items-start justify-between">
          <div>
            <div
              style={{
                fontSize: "10px",
                fontWeight: 700,
                letterSpacing: "1.5px",
                color: Theme().primary,
                fontFamily: "Inter, sans-serif",
                textTransform: "uppercase",
                marginBottom: "6px",
              }}
            >
              Executive Analytics Stethoscope
            </div>
            <div
              style={{
                fontSize: "20px",
                fontWeight: 700,
                color: Theme().textPrimary,
                fontFamily: "Inter, sans-serif",
                lineHeight: 1.3,
              }}
            >
              Core Strategic & Operational Progress
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "#888",
                fontFamily: "Inter, sans-serif",
                marginTop: "4px",
              }}
            >
              Interactive analytical charts for cross-department review and retention tracking.
            </div>
          </div>

          {/* Tab buttons */}
          <div className="flex items-center gap-1">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs transition-all"
                style={{
                  backgroundColor: "white",
                  border: activeTab === tab.key ? "2px solid #1e293b" : "2px solid transparent",
                  color: activeTab === tab.key ? Theme().textPrimary : "#888",
                  fontFamily: "Inter, sans-serif",
                  fontWeight: activeTab === tab.key ? 600 : 400,
                  fontSize: "12px",
                }}
              >
                <span style={{ color: activeTab === tab.key ? Theme().primary : "#aaa" }}>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Chart Content */}
      <div className="px-6 pb-6">
        {loading ? (
          <div className="flex justify-center items-center" style={{ height: "280px" }}>
            <Spin />
          </div>
        ) : (
          <div className="flex gap-6">
            {/* Chart area */}
            <div className="flex-1" style={{ minWidth: 0, overflowX: "auto" }}>
              {activeTab === "health" && areaChartData.length > 0 && (
                <div style={{ minWidth: "500px" }}>
                  <Area {...areaConfig} />
                </div>
              )}
              {activeTab === "dept" && columnChartData.length > 0 && (
                <div style={{ minWidth: `${Math.max(600, deptData.length * 140)}px` }}>
                  <Column {...columnConfig} />
                </div>
              )}
              {activeTab === "health" && areaChartData.length === 0 && (
                <div
                  className="flex items-center justify-center"
                  style={{ height: "260px", color: "#aaa", fontFamily: "Inter, sans-serif", fontSize: "13px" }}
                >
                  No performance data available yet for this cycle. Update plan progress to see trends.
                </div>
              )}
              {activeTab === "dept" && columnChartData.length === 0 && (
                <div
                  className="flex items-center justify-center"
                  style={{ height: "260px", color: "#aaa", fontFamily: "Inter, sans-serif", fontSize: "13px" }}
                >
                  No department data available. Plans must be assigned to units.
                </div>
              )}
            </div>

            {/* Right sidebar info panel */}
            <div style={{ width: "220px", flexShrink: 0, display: "flex", alignItems: "center" }}>
              {activeTab === "health" && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        backgroundColor: Theme().primary,
                      }}
                    />
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: 700,
                        color: Theme().textPrimary,
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      Operational Pulse
                    </span>
                  </div>
                  <p
                    style={{
                      fontSize: "11px",
                      color: "#666",
                      fontFamily: "Inter, sans-serif",
                      lineHeight: 1.5,
                      marginBottom: "16px",
                    }}
                  >
                    Overall operational health indices have progressed by{" "}
                    <strong>{latestHealth.toFixed(1)}%</strong> over the cycle period, driven by
                    strategic growth metrics.
                  </p>
                  <div
                    className="rounded-lg px-3 py-2"
                    style={{
                      backgroundColor: `rgba(${healthColorRgb},0.06)`,
                      border: `1px solid rgba(${healthColorRgb},0.2)`,
                    }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span style={{ fontSize: "12px", color: healthColor, fontFamily: "Inter, sans-serif" }}>
                        Health Index
                      </span>
                      <span style={{ fontSize: "14px", fontWeight: 700, color: healthColor, fontFamily: "Inter, sans-serif" }}>
                        {latestHealth.toFixed(1)}%
                      </span>
                    </div>
                    <div style={{ width: "100%", height: "4px", backgroundColor: `rgba(${healthColorRgb},0.15)`, borderRadius: "2px" }}>
                      <div style={{ width: `${Math.min(latestHealth, 100)}%`, height: "100%", backgroundColor: healthColor, borderRadius: "2px" }} />
                    </div>
                  </div>
                  <p
                    style={{
                      fontSize: "10px",
                      color: "#999",
                      fontFamily: "Inter, sans-serif",
                      marginTop: "12px",
                      lineHeight: 1.4,
                      fontStyle: "italic",
                    }}
                  >
                    * Velocity index indicates current delivery sprint speeds of tactical and sales
                    pipelines combined.
                  </p>
                </div>
              )}

              {activeTab === "dept" && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        backgroundColor: Theme().primary,
                      }}
                    />
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: 700,
                        color: Theme().textPrimary,
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      Alignment Breakdown
                    </span>
                  </div>
                  <p
                    style={{
                      fontSize: "11px",
                      color: "#666",
                      fontFamily: "Inter, sans-serif",
                      lineHeight: 1.5,
                      marginBottom: "16px",
                    }}
                  >
                    Click on the <strong style={{ color: Theme().primary }}>Strategic Alignment</strong> bars
                    of any department to isolate their specific plans below.
                  </p>
                  {deptData.length > 0 && (
                    <div
                      className="rounded-lg p-3"
                      style={{
                        backgroundColor: "rgba(241, 90, 36, 0.04)",
                        border: `1px solid ${Theme().navBorder}`,
                      }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span style={{ fontSize: "11px", fontWeight: 600, color: Theme().textPrimary, fontFamily: "Inter, sans-serif" }}>
                          Avg. Strategic Alignment
                        </span>
                        <span style={{ fontSize: "13px", fontWeight: 700, color: Theme().primary, fontFamily: "Inter, sans-serif" }}>
                          {(deptData.reduce((s, d) => s + d.strategicAlignment, 0) / deptData.length).toFixed(1)}%
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span style={{ fontSize: "11px", fontWeight: 600, color: Theme().textPrimary, fontFamily: "Inter, sans-serif" }}>
                          Avg. Efficiency
                        </span>
                        <span style={{ fontSize: "13px", fontWeight: 700, color: "#22c55e", fontFamily: "Inter, sans-serif" }}>
                          {(deptData.reduce((s, d) => s + d.efficiencyIndex, 0) / deptData.length).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ExecutiveStethoscope;
